import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { filter, Subscription } from 'rxjs';

import { FRONT_LOADING_PATHS } from 'src/app/front-loading/routes';
import { selectSessionActive } from 'src/app/stores/global';
import { selectSettingDevice } from 'src/app/stores/settings';
import { SUPPORT_TOOL_URL } from 'src/environments/runtime';

import { IMenuOption } from '../../interfaces/IMenuOption';
import { DEVICE_TYPES, DeviceType } from '../../interfaces/ISettings';
import {
  ACTION_TYPES,
  DEVICE_VIEW_CONTEXT_MAP,
  FRONT_PATHS,
  ICONS,
  REAR_PATHS,
  RETAIL_PATHS,
  TTW_PATHS,
  VIEW_CALLING,
  VIEW_TYPES,
} from '../../utils/constants';
import { Utils } from '../../utils/utils';

@Component({
  selector: 'frodo-left-menu',
  templateUrl: './left-menu.component.html',
  styleUrls: ['./left-menu.component.scss'],
})
export class LeftMenuComponent implements OnInit, OnDestroy {
  CONFIGURATION_CONTEXT = 'setup';
  DEFAULT_CONTEXT = VIEW_TYPES.REAR_LOADING;

  isMenuExpanded = false;
  isDeviceActivated = false;
  isSessionActive = false;
  showhideMenu = '/assets/arrow-right.svg';
  frodoContext: string;
  currentRoute: string;
  screenType: string;
  path: string;
  presence: boolean;
  spaceNumber: string;
  scheduleId: string;
  spaceId: string;
  action: string;
  menuOptions: Array<object>;
  deviceType: DeviceType;
  subscription: Subscription = new Subscription();

  private homeText = 'common.home';
  private lockerStatusText = 'common.lockerStatusTitle';
  private setupText = 'common.setup';
  private helpCenterText = 'common.helpCenterTitle';
  private supportToolText = 'common.supportTool';
  private coreText = 'common.core';
  private employeeText = 'common.employee';

  constructor(private router: Router, private utils: Utils, private store: Store) {}

  ngOnInit() {
    this.subscription.add(
      this.store.select(selectSettingDevice).subscribe((device) => {
        this.isDeviceActivated = device.isActivated;
        this.deviceType = device.deviceType;
        this.DEFAULT_CONTEXT = DEVICE_VIEW_CONTEXT_MAP[this.deviceType] ?? VIEW_TYPES.REAR_LOADING;
      })
    );

    this.subscription.add(
      this.store.select(selectSessionActive).subscribe((isSessionActive) => {
        this.isSessionActive = isSessionActive;
        this.getValidations();
      })
    );

    this.subscription.add(
      this.router.events
        .pipe(filter((event) => event instanceof NavigationEnd))
        .subscribe((navigation: NavigationEnd) => {
          this.updateCurrentScreenFromSnapshot();
          this.processRoute(navigation.urlAfterRedirects);
        })
    );

    this.updateCurrentScreenFromSnapshot();
    this.processRoute(this.router.url);
  }

  private processRoute(url: string) {
    this.currentRoute = url;
    this.frodoContext = url.startsWith('/') ? url.slice(1) : url;
    const slash = this.frodoContext.indexOf('/');
    const semicolon = this.frodoContext.indexOf(';');
    if (slash > 0) {
      this.frodoContext = this.frodoContext.substring(0, slash);
    } else if (semicolon > 0) {
      this.frodoContext = this.frodoContext.substring(0, semicolon);
    }
    this.getValidations();
  }

  private updateCurrentScreenFromSnapshot(): void {
    let route = this.router.routerState.snapshot.root;
    while (route.firstChild) {
      route = route.firstChild;
    }

    this.path = route.routeConfig?.path ?? '';
    this.screenType = route.params?.type ?? '';
    this.presence = route.params?.presence === 'true';
    this.spaceNumber = route.params?.spaceNumber ?? '';
    this.scheduleId = route.params?.scheduleId ?? '';
    this.action = route.params?.action ?? '';
    this.spaceId = route.params?.spaceId ?? '';
  }

  getValidations() {
    this.menuOptions = this.getMenuOptions();
  }

  getMenuOptions() {
    return [
      this.buildHomeOption(),
      this.buildStatusOption(),
      this.buildSetupOption(),
      this.buildHelpCenterOption(),
      this.buildSupportToolOption(),
      this.buildCoreOption(),
      this.buildEmployeeOption(),
    ];
  }

  private getDeviceContext(): string {
    return this.frodoContext === VIEW_TYPES.TTW ? this.frodoContext : this.getContextUrl();
  }

  private getHomeUrl(deviceContext: string): string {
    return this.isArrayFlDevice() ? VIEW_TYPES.FRONT_LOADING : deviceContext;
  }

  private isArrayFlDeviceOnTtw(): boolean {
    return this.isArrayFlDevice() && this.frodoContext === VIEW_TYPES.TTW;
  }

  private isArrayFlDevice(): boolean {
    return this.deviceType === DEVICE_TYPES.ARRAY_FL;
  }

  private isSessionRestrictedContext(): boolean {
    return (
      this.frodoContext === VIEW_TYPES.FRONT_LOADING || this.frodoContext === VIEW_TYPES.RETAIL
    );
  }

  private isOnSubPage(subPage: string): boolean {
    return this.currentRoute === `/${this.frodoContext}/${subPage}`;
  }

  private pickIcon(isActive: boolean, activeIcon: string, inactiveIcon: string): string {
    return isActive ? activeIcon : inactiveIcon;
  }

  private buildHomeOption() {
    return {
      name: this.homeText,
      url: this.getHomeUrl(this.getDeviceContext()),
      icon: this.getHomeIcon(this.isDeviceActivated),
      selected: this.isFrodoContext(),
      visible: true,
      disable: !this.isDeviceActivated,
      isRoot: true,
      id: 'nav_home',
    };
  }

  private buildStatusOption() {
    const isActive = this.isOnSubPage('status');
    return {
      name: this.lockerStatusText,
      url: 'status',
      icon: this.pickIcon(isActive, ICONS.STATUS_ACTIVE, ICONS.STATUS),
      selected: isActive,
      visible: this.frodoContext === VIEW_TYPES.REAR_LOADING,
      id: 'nav_status',
    };
  }

  private buildSetupOption() {
    const isActive = this.isConfigurationContext();
    return {
      name: this.setupText,
      url: 'setup',
      icon: this.pickIcon(isActive, ICONS.SETUP_ACTIVE, ICONS.SETUP),
      selected: isActive,
      visible:
        this.frodoContext !== VIEW_TYPES.TTW &&
        (!this.isSessionRestrictedContext() || this.isSessionActive || !this.isDeviceActivated),
      disable: false,
      isRoot: true,
      id: 'nav_setup',
    };
  }

  private buildHelpCenterOption() {
    return {
      name: this.helpCenterText,
      url: 'help-center',
      icon: this.getHelpIcon(this.isDeviceActivated),
      selected: this.isOnSubPage('help-center'),
      visible: true,
      disable: !this.isDeviceActivated,
      id: 'nav_help',
    };
  }

  private buildSupportToolOption() {
    const deviceContext = this.getDeviceContext();
    const isSupportToolHidden =
      this.frodoContext === VIEW_TYPES.RETAIL ||
      this.frodoContext === 'setup' ||
      this.frodoContext === VIEW_TYPES.TTW;
    const isRearLoading = deviceContext === VIEW_TYPES.REAR_LOADING;

    return {
      name: this.supportToolText,
      url: `${SUPPORT_TOOL_URL}?deviceContext=${deviceContext}${isRearLoading ? '&rear=true' : ''}`,
      icon: ICONS.SUPPORT_TOOL,
      selected: false,
      visible:
        this.frodoContext !== VIEW_TYPES.TTW &&
        (!isSupportToolHidden || this.isArrayFlDeviceOnTtw()) &&
        (!this.isSessionRestrictedContext() || this.isSessionActive),
      id: 'nav_support_tool',
      isExternal: true,
      disable: !this.isDeviceActivated,
    };
  }

  private buildCoreOption() {
    const isActive = this.isOnSubPage('password') || this.isOnSubPage('core');
    return {
      name: this.coreText,
      url: 'password',
      icon: this.pickIcon(isActive, ICONS.CORE_ACTIVE, ICONS.CORE),
      selected: isActive,
      visible: this.frodoContext === VIEW_TYPES.RETAIL,
      disable: !this.isDeviceActivated,
      id: 'nav_core',
    };
  }

  private buildEmployeeOption() {
    const isActive = this.isOnSubPage(FRONT_PATHS.ASSOCIATE_ID);
    return {
      name: this.employeeText,
      url: `${VIEW_TYPES.FRONT_LOADING}/${FRONT_PATHS.ASSOCIATE_ID}`,
      icon: this.pickIcon(isActive, ICONS.EMPLOYEE_ACTIVE, ICONS.EMPLOYEE),
      selected: isActive,
      visible:
        this.isArrayFlDevice() &&
        (this.frodoContext === VIEW_TYPES.FRONT_LOADING || this.isArrayFlDeviceOnTtw()),
      disable: !this.isDeviceActivated,
      isRoot: true,
      id: 'nav_employee',
    };
  }

  getHomeIcon(isActivated: boolean) {
    if (this.isFrodoContext()) {
      return ICONS.HOME_ACTIVE;
    }
    return !isActivated ? ICONS.HOME_DISABLED : ICONS.HOME;
  }

  isFrodoContext() {
    const isAtHomeUrl = this.currentRoute === `/${this.getHomeUrl(this.getDeviceContext())}`;
    const isArrayFlAtFrontLoadingRoot =
      this.isArrayFlDevice() && this.currentRoute === `/${VIEW_TYPES.FRONT_LOADING}`;

    return (
      (isAtHomeUrl || isArrayFlAtFrontLoadingRoot) &&
      this.frodoContext !== this.CONFIGURATION_CONTEXT
    );
  }

  getContextUrl() {
    return this.DEFAULT_CONTEXT;
  }

  getHelpIcon(isActivated: boolean) {
    if (this.isOnSubPage('help-center')) {
      return ICONS.HELP_ACTIVE;
    }
    return !isActivated ? ICONS.HELP_DISABLED : ICONS.HELP;
  }

  isConfigurationContext() {
    const currentRoute = this.currentRoute || '';
    return (
      currentRoute.includes(this.CONFIGURATION_CONTEXT) &&
      !this.currentRoute.includes('help-center')
    );
  }

  setActiveClass(): void {
    if (this.isMenuExpanded) {
      this.isMenuExpanded = false;
      this.showhideMenu = ICONS.ARROW_RIGHT;
    } else {
      this.isMenuExpanded = true;
      this.showhideMenu = ICONS.ARROW_LEFT;
    }
  }

  redirect(targetScreen: string, isRoot = false) {
    if (this.path === VIEW_CALLING.LOADING_ORDER) {
      this.utils.cancelLoading({
        frodoContext: this.frodoContext,
        targetScreen: isRoot ? '' : targetScreen,
        type: this.screenType,
        scheduleId: this.scheduleId,
      });
    } else {
      const url = isRoot ? `${targetScreen}` : `${this.frodoContext}/${targetScreen}`;
      this.utils.redirect(url);
    }
  }

  private retailLoadingInProgress(): boolean {
    const deliveryScreens = [
      RETAIL_PATHS.DELIVERY_OPTIONS,
      RETAIL_PATHS.SIZE_SELECTOR,
      RETAIL_PATHS.COMPARTMENT_INDICATOR,
    ];
    let deliveryScreenFound = false;
    deliveryScreens.every((screen: string) => {
      const isRetail = this.frodoContext === VIEW_TYPES.RETAIL;
      const isCompartmentIndicator = this.path === RETAIL_PATHS.COMPARTMENT_INDICATOR;
      const isDeliveryAction = this.currentRoute.includes(`action=${ACTION_TYPES.DELIVERY}`);
      const isSameScreen = this.path === screen;

      if (
        isRetail &&
        ((isCompartmentIndicator && isDeliveryAction) || (!isCompartmentIndicator && isSameScreen))
      ) {
        deliveryScreenFound = true;
        return false;
      }
      return true;
    });
    return deliveryScreenFound;
  }

  private rearLoadingInProgress(): boolean {
    const deliveryScreens = [
      REAR_PATHS.SIZE_SELECTOR,
      REAR_PATHS.LOADING_ORDER,
      REAR_PATHS.PROCESSING_ORDER,
    ];
    const isRear = this.frodoContext === VIEW_TYPES.REAR_LOADING;
    return isRear && deliveryScreens.some((screen) => this.path === screen);
  }

  private isPickupInProgress(): boolean {
    const retailPickupInProgress =
      this.path == RETAIL_PATHS.COMPARTMENT_INDICATOR &&
      (this.action === ACTION_TYPES.PICKUP || this.action === ACTION_TYPES.RECLAIM);
    const ttwPickupInProgress =
      this.frodoContext === VIEW_TYPES.TTW && this.path === TTW_PATHS.DISPENSING_ORDER;

    return retailPickupInProgress || ttwPickupInProgress;
  }

  private isFrontLoadingInProgress(): boolean {
    const deliveryScreens = [
      FRONT_LOADING_PATHS.GUIDED_LOADING_ORDER,
      FRONT_LOADING_PATHS.LOAD_CONFIRMATION,
      FRONT_LOADING_PATHS.SIZE_SELECTOR,
      FRONT_LOADING_PATHS.LOADING_ORDER,
      FRONT_LOADING_PATHS.PROCESSING_ORDER,
    ];
    const isFrontLoading = this.frodoContext === VIEW_TYPES.FRONT_LOADING;
    return isFrontLoading && deliveryScreens.some((screen) => this.path === screen);
  }

  private isNavigationDisabled(): boolean {
    return (
      (this.isArrayFlDevice() && this.isFrontLoadingInProgress()) ||
      this.isPickupInProgress() ||
      this.retailLoadingInProgress() ||
      this.rearLoadingInProgress()
    );
  }

  onItemClick(event: MouseEvent, item: IMenuOption): void {
    if (item.disable || this.isNavigationDisabled()) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (item.isExternal) return;
    this.redirect(item.url, item.isRoot);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
}
